import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import TradingModal from '../../../components/TradingModal';
import MarketWatchSettingsModal from './MarketWatchSettingsModal';
import tradingApi from '../../../services/tradingApi';
import useLanguage from '../../../hooks/useLanguage';

const SymbolList = ({ onSymbolSelect, selectedSymbol }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tradingModalOpen, setTradingModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [tradingType, setTradingType] = useState('buy');
  const [selectedSymbolForTrade, setSelectedSymbolForTrade] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  const { t, isRTL } = useLanguage();

  useEffect(() => {
    initializeSymbolList();
    connectToWebSocket();

    return () => {
      tradingApi?.disconnectWebSocket();
    };
  }, []);

  const initializeSymbolList = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch symbols from /public/symbols endpoint
      const response = await fetch('/symbols/index.json');
      
      if (!response?.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const symbolsData = await response?.json();
      
      if (Array.isArray(symbolsData)) {
        // Process API response
        const processedSymbols = symbolsData?.map(symbol => ({
          symbol: symbol?.symbol || 'UNKNOWN',
          bid: parseFloat(symbol?.bid) || 0,
          ask: parseFloat(symbol?.ask) || 0,
          change: parseFloat(symbol?.change) || 0,
          changePercent: parseFloat(symbol?.changePercent) || 0,
          volume: parseInt(symbol?.volume) || 0,
          category: symbol?.category || 'forex',
          isFavorite: false, // Default to false, can be loaded from user preferences
          description: symbol?.description || symbol?.symbol
        }));
        
        setSymbols(processedSymbols);
        console.log('Loaded', processedSymbols?.length, 'symbols from /public/symbols endpoint');
      } else {
        throw new Error('Invalid response format from symbols endpoint');
      }
    } catch (apiError) {
      console.warn('Public symbols endpoint loading failed, using fallback data:', apiError);
      setError('Symbols endpoint failed - using demo data');
      
      // Keep existing fallback mock data
      const mockSymbols = [
        { symbol: 'EURUSD', bid: 1.16745, ask: 1.16753, change: 0.0008, changePercent: 0.07, volume: 1250000, isFavorite: true, category: 'forex' },
        { symbol: 'GBPUSD', bid: 1.34281, ask: 1.34289, change: -0.0012, changePercent: -0.09, volume: 980000, isFavorite: true, category: 'forex' },
        { symbol: 'XAUUSD', bid: 1935.02, ask: 1935.23, change: 12.45, changePercent: 0.64, volume: 750000, isFavorite: true, category: 'metals' },
        { symbol: 'XAGUSD', bid: 48.329, ask: 48.342, change: -0.234, changePercent: -0.48, volume: 320000, isFavorite: false, category: 'metals' },
        { symbol: 'GBPJPY', bid: 201.887, ask: 201.903, change: 0.456, changePercent: 0.23, volume: 560000, isFavorite: true, category: 'forex' },
        { symbol: 'GER30', bid: 23823.8, ask: 23825.4, change: -45.2, changePercent: -0.28, volume: 420000, isFavorite: true, category: 'indices' },
        { symbol: 'US500', bid: 6135.6, ask: 6136.2, change: 23.4, changePercent: 0.57, volume: 890000, isFavorite: false, category: 'indices' },
        { symbol: 'USDJPY', bid: 151.234, ask: 151.247, change: 0.456, changePercent: 0.30, volume: 456789, isFavorite: false, category: 'forex' },
        { symbol: 'AUDUSD', bid: 0.65432, ask: 0.65438, change: -0.0015, changePercent: -0.23, volume: 345670, isFavorite: true, category: 'forex' },
        { symbol: 'BTCUSD', bid: 43250.00, ask: 43275.00, change: 1250.00, changePercent: 2.98, volume: 125000, isFavorite: false, category: 'crypto' },
        { symbol: 'ETHUSD', bid: 2485.75, ask: 2487.25, change: 85.50, changePercent: 3.56, volume: 89000, isFavorite: true, category: 'crypto' }
      ];
      
      setSymbols(mockSymbols);
    } finally {
      setLoading(false);
    }
  };

  const connectToWebSocket = () => {
    try {
      setConnectionStatus('connecting');
      
      // Connect to Zonex WebSocket for real-time price updates
      tradingApi?.connectWebSocket(
        (data) => handleWebSocketMessage(data),
        (error) => {
          console.error('WebSocket error:', error);
          setConnectionStatus('error');
          simulatePriceUpdates(); // Fallback to simulation
        },
        (event) => {
          console.log('WebSocket closed:', event);
          setConnectionStatus('disconnected');
        }
      );

      // Subscribe to price updates for all symbols
      setTimeout(() => {
        if (tradingApi?.getConnectionStatus() === 'connected') {
          const symbolNames = symbols?.map(s => s?.symbol);
          tradingApi?.subscribeToPrices(symbolNames);
          setConnectionStatus('connected');
          console.log('Subscribed to price updates for', symbolNames?.length, 'symbols');
        } else {
          // Fallback to simulated updates
          simulatePriceUpdates();
        }
      }, 1500);

    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setConnectionStatus('error');
      simulatePriceUpdates();
    }
  };

  const handleWebSocketMessage = (data) => {
    switch (data?.type) {
      case 'price_update':
        updateSymbolPrice(data);
        break;
      case 'connection_status': setConnectionStatus(data?.status ||'connected');
        break;
      default:
        // Handle other message types as needed
        break;
    }
  };

  const updateSymbolPrice = (priceData) => {
    setSymbols(prev => 
      prev?.map(symbol => {
        if (symbol?.symbol === priceData?.symbol) {
          return {
            ...symbol,
            bid: parseFloat(priceData?.bid) || symbol?.bid,
            ask: parseFloat(priceData?.ask) || symbol?.ask,
            change: parseFloat(priceData?.change) || symbol?.change,
            changePercent: parseFloat(priceData?.changePercent) || symbol?.changePercent,
            volume: parseInt(priceData?.volume) || symbol?.volume
          };
        }
        return symbol;
      })
    );
  };

  // Simulate real-time price updates when WebSocket is not available
  const simulatePriceUpdates = () => {
    setConnectionStatus('simulated');
    
    const updateInterval = setInterval(() => {
      setSymbols(prev => 
        prev?.map(symbol => {
          const bidChange = (Math.random() - 0.5) * 0.001;
          const askChange = bidChange + (Math.random() * 0.0005);
          const newBid = Math.max(0, symbol?.bid + bidChange);
          const newAsk = Math.max(0, symbol?.ask + askChange);
          
          return {
            ...symbol,
            bid: newBid,
            ask: newAsk,
            change: symbol?.change + (Math.random() - 0.5) * 0.01,
            changePercent: symbol?.changePercent + (Math.random() - 0.5) * 0.05,
            volume: symbol?.volume + Math.floor((Math.random() - 0.5) * 10000)
          };
        })
      );
    }, 2000);

    return () => clearInterval(updateInterval);
  };

  const toggleFavorite = (symbolName) => {
    setSymbols(prev => 
      prev?.map(symbol => 
        symbol?.symbol === symbolName 
          ? { ...symbol, isFavorite: !symbol?.isFavorite }
          : symbol
      )
    );
    
    // TODO: Save favorite status to user preferences via API
    console.log('Toggled favorite for', symbolName);
  };

  const handleSymbolClick = (symbol) => {
    // Don't make XHR request, instead send WebSocket payload for chart data
    if (tradingApi?.ws && tradingApi?.getConnectionStatus() === 'connected') {
      const payload = {
        "symbols": false,
        "symbol": symbol?.symbol || "BTCUSD",
        "single": true,
        "chart": true,
        "period": "M1",
        "positions": true,
        "token": "92b79d3a-3d52-412c-ac67-00c5cdbf4bf5",
        "company": "zonex"
      };
      
      // Send payload via WebSocket
      tradingApi?.ws?.send(JSON.stringify(payload));
      console.log('Sent WebSocket chart request for:', symbol?.symbol, payload);
      
      // Call the parent callback to update selected symbol
      onSymbolSelect?.(symbol);
    } else {
      console.warn('WebSocket not connected, cannot send chart request');
      // Still update the selected symbol
      onSymbolSelect?.(symbol);
    }
  };

  const filteredSymbols = symbols?.filter(symbol => {
    const matchesSearch = symbol?.symbol?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesTab = activeTab === 'all' || 
                      (activeTab === 'favorites' && symbol?.isFavorite);
    return matchesSearch && matchesTab;
  });

  const formatPrice = (price) => {
    if (price > 1000) return price?.toFixed(1);
    if (price > 10) return price?.toFixed(3);
    return price?.toFixed(5);
  };

  const calculateSpread = (bid, ask) => {
    return (ask - bid)?.toFixed(5);
  };

  const getChangeColor = (change) => {
    return change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-500';
      case 'connecting': return 'text-yellow-500';
      case 'simulated': return 'text-blue-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const handleTradeClick = (symbol, type, event) => {
    event?.stopPropagation();
    setSelectedSymbolForTrade(symbol);
    setTradingType(type);
    setTradingModalOpen(true);
  };

  if (loading) {
    return (
      <div className={`h-full bg-card flex flex-col items-center justify-center ${isRTL ? 'rtl' : ''}`}>
        <Icon name="Loader" size={32} className="text-muted-foreground mb-4 animate-spin" />
        <p className="text-muted-foreground text-sm">{t('loadingSymbols') || 'Loading symbols...'}</p>
      </div>
    );
  }

  return (
    <div className={`h-full bg-card flex flex-col ${isRTL ? 'rtl' : ''}`}>
      {/* Header */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-foreground">{t('marketWatch')}</h2>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' : 
                connectionStatus === 'connecting' ? 'bg-yellow-500' : 
                connectionStatus === 'simulated'? 'bg-blue-500' : 'bg-red-500'
              }`}></div>
              <span className={`text-xs ${getConnectionStatusColor()}`}>
                {connectionStatus === 'simulated' ? 'DEMO' : t(connectionStatus)}
              </span>
            </div>
            <Icon 
              name="Settings" 
              size={16} 
              className="text-muted-foreground cursor-pointer hover:text-foreground" 
              onClick={() => setSettingsModalOpen(true)}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-600">
            {error}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-3">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('searchSymbols')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-muted p-1 rounded text-xs">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 py-1.5 px-2 font-medium rounded transition-colors ${
              activeTab === 'all' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('all')}
          </button>
          <button
            onClick={() => setActiveTab('favorites')}
            className={`flex-1 py-1.5 px-2 font-medium rounded transition-colors ${
              activeTab === 'favorites' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('favorites')}
          </button>
        </div>
      </div>

      {/* Column Headers - Enhanced with Buy/Sell columns */}
      <div className="px-3 py-2 border-b border-border bg-muted/50">
        <div className="grid grid-cols-5 gap-2 text-xs font-medium text-muted-foreground">
          <div className="col-span-2">{t('symbol')}</div>
          <div className="text-center">{t('sell')}</div>
          <div className="text-center">{t('buy')}</div>
          <div className="text-center">{t('spread')}</div>
        </div>
      </div>

      {/* Symbol List - Enhanced with Buy/Sell prices and clickable buttons */}
      <div className="flex-1 overflow-y-auto">
        {filteredSymbols?.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <Icon name="Search" size={32} className="text-muted-foreground mb-2 mx-auto" />
              <p className="text-sm text-muted-foreground">{t('noSymbolsFound') || 'No symbols found'}</p>
            </div>
          </div>
        ) : (
          filteredSymbols?.map((symbol) => (
            <div
              key={symbol?.symbol}
              onClick={() => handleSymbolClick(symbol)}
              className={`cursor-pointer hover:bg-muted/50 transition-colors border-b border-border/50 ${
                selectedSymbol?.symbol === symbol?.symbol ? 'bg-muted border-l-2 border-l-primary' : ''
              }`}
            >
              <div className="px-3 py-2.5">
                <div className="grid grid-cols-5 gap-2 items-center text-xs">
                  {/* Symbol Column with Favorite Star */}
                  <div className="col-span-2 flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        toggleFavorite(symbol?.symbol);
                      }}
                      className="hover:bg-background rounded p-0.5"
                    >
                      <Icon 
                        name="Star" 
                        size={12} 
                        className={symbol?.isFavorite ? 'text-amber-500 fill-current' : 'text-muted-foreground'} 
                      />
                    </button>
                    <div className="min-w-0">
                      <div className="font-medium text-foreground truncate text-xs">{symbol?.symbol}</div>
                      <div className={`font-mono text-xs ${getChangeColor(symbol?.change)}`}>
                        {symbol?.change >= 0 ? '+' : ''}{symbol?.changePercent?.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                  
                  {/* Sell Price - Clickable */}
                  <div className="text-center">
                    <button
                      onClick={(e) => handleTradeClick(symbol, 'sell', e)}
                      className="w-full py-1 px-2 font-mono text-foreground font-medium text-xs hover:bg-red-500/10 rounded transition-colors"
                    >
                      {formatPrice(symbol?.bid)}
                    </button>
                  </div>
                  
                  {/* Buy Price - Clickable */}
                  <div className="text-center">
                    <button
                      onClick={(e) => handleTradeClick(symbol, 'buy', e)}
                      className="w-full py-1 px-2 font-mono text-foreground font-medium text-xs hover:bg-green-500/10 rounded transition-colors"
                    >
                      {formatPrice(symbol?.ask)}
                    </button>
                  </div>
                  
                  {/* Spread */}
                  <div className="text-center">
                    <div className="font-mono text-muted-foreground text-xs">
                      {calculateSpread(symbol?.bid, symbol?.ask)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Trading Modal */}
      <TradingModal
        isOpen={tradingModalOpen}
        onClose={() => setTradingModalOpen(false)}
        type={tradingType}
        symbol={selectedSymbolForTrade}
      />

      {/* Settings Modal */}
      <MarketWatchSettingsModal
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
      />
    </div>
  );
};

export default SymbolList;